import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { 
  Type, 
  Contrast, 
  Accessibility, 
  X, 
  RotateCcw,
  Eye,
  Keyboard,
  Volume2
} from 'lucide-react';

interface AccessibilityToolbarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AccessibilityToolbar({ isOpen, onClose }: AccessibilityToolbarProps) {
  const { t } = useTranslation();
  const { settings, updateSetting, resetSettings, announceToScreenReader } = useAccessibility();

  if (!isOpen) return null;

  const handleFontSizeChange = () => {
    const sizes = ['normal', 'large', 'extra-large'] as const;
    const currentIndex = sizes.indexOf(settings.fontSize);
    const nextIndex = (currentIndex + 1) % sizes.length;
    const newSize = sizes[nextIndex];
    
    console.log('🔧 Changing font size from', settings.fontSize, 'to', newSize);
    updateSetting('fontSize', newSize);
    
    // Show immediate visual feedback
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #2563eb;
      color: white;
      padding: 20px 30px;
      border-radius: 12px;
      font-size: 18px;
      font-weight: 600;
      z-index: 10001;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      animation: fadeInOut 2s ease-in-out;
    `;
    
    const sizeLabels = {
      normal: t('accessibility.fontSize.normal', 'רגיל'),
      large: t('accessibility.fontSize.large', 'גדול'),
      'extra-large': t('accessibility.fontSize.extraLarge', 'גדול מאוד')
    };
    
    notification.textContent = `${t('accessibility.fontSize.label', 'גודל גופן')}: ${sizeLabels[newSize]}`;
    
    // Add animation keyframes if not already added
    if (!document.getElementById('accessibility-animations')) {
      const style = document.createElement('style');
      style.id = 'accessibility-animations';
      style.textContent = `
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
          20% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 2000);
  };

  const getFontSizeLabel = () => {
    switch (settings.fontSize) {
      case 'normal': return t('accessibility.fontSize.normal', 'רגיל');
      case 'large': return t('accessibility.fontSize.large', 'גדול');
      case 'extra-large': return t('accessibility.fontSize.extraLarge', 'גדול מאוד');
      default: return t('accessibility.fontSize.normal', 'רגיל');
    }
  };

  return (
    <div 
      className="fixed top-4 right-4 z-50"
      role="dialog"
      aria-labelledby="accessibility-toolbar-title"
      aria-describedby="accessibility-toolbar-description"
    >
      <Card className="w-80 shadow-lg border-2 border-blue-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Accessibility className="h-5 w-5 text-blue-600" />
              <h2 
                id="accessibility-toolbar-title"
                className="text-lg font-semibold"
              >
                {t('accessibility.toolbar.title', 'נגישות')}
              </h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label={t('accessibility.toolbar.close', 'סגור תפריט נגישות')}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <p 
            id="accessibility-toolbar-description" 
            className="text-sm text-muted-foreground mb-4"
          >
            {t('accessibility.toolbar.description', 'התאם את האתר לצרכים שלך')}
          </p>

          <div className="space-y-3">
            {/* Font Size Control */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium flex items-center gap-2">
                <Type className="h-4 w-4" />
                {t('accessibility.fontSize.label', 'גודל גופן')}
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleFontSizeChange}
                aria-label={t('accessibility.fontSize.changeLabel', 'שנה גודל גופן, נוכחי: {{size}}', { size: getFontSizeLabel() })}
              >
                {getFontSizeLabel()}
              </Button>
            </div>

            <Separator />

            {/* High Contrast Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium flex items-center gap-2">
                <Contrast className="h-4 w-4" />
                {t('accessibility.contrast.label', 'ניגודיות גבוהה')}
              </label>
              <Button
                variant={settings.highContrast ? "default" : "outline"}
                size="sm"
                onClick={() => updateSetting('highContrast', !settings.highContrast)}
                aria-label={settings.highContrast 
                  ? t('accessibility.contrast.disable', 'השבת ניגודיות גבוהה')
                  : t('accessibility.contrast.enable', 'הפעל ניגודיות גבוהה')
                }
                aria-pressed={settings.highContrast}
              >
                {settings.highContrast 
                  ? t('accessibility.enabled', 'מופעל')
                  : t('accessibility.disabled', 'מושבת')
                }
              </Button>
            </div>

            <Separator />

            {/* Reduced Motion Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium flex items-center gap-2">
                <Eye className="h-4 w-4" />
                {t('accessibility.motion.label', 'הפחת תנועה')}
              </label>
              <Button
                variant={settings.reducedMotion ? "default" : "outline"}
                size="sm"
                onClick={() => updateSetting('reducedMotion', !settings.reducedMotion)}
                aria-label={settings.reducedMotion
                  ? t('accessibility.motion.disable', 'השבת הפחתת תנועה')
                  : t('accessibility.motion.enable', 'הפעל הפחתת תנועה')
                }
                aria-pressed={settings.reducedMotion}
              >
                {settings.reducedMotion
                  ? t('accessibility.enabled', 'מופעל')
                  : t('accessibility.disabled', 'מושבת')
                }
              </Button>
            </div>

            <Separator />

            {/* Screen Reader Mode Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium flex items-center gap-2">
                <Volume2 className="h-4 w-4" />
                {t('accessibility.screenReader.label', 'מצב קורא מסך')}
              </label>
              <Button
                variant={settings.screenReaderMode ? "default" : "outline"}
                size="sm"
                onClick={() => updateSetting('screenReaderMode', !settings.screenReaderMode)}
                aria-label={settings.screenReaderMode
                  ? t('accessibility.screenReader.disable', 'השבת מצב קורא מסך')
                  : t('accessibility.screenReader.enable', 'הפעל מצב קורא מסך')
                }
                aria-pressed={settings.screenReaderMode}
              >
                {settings.screenReaderMode
                  ? t('accessibility.enabled', 'מופעל')
                  : t('accessibility.disabled', 'מושבת')
                }
              </Button>
            </div>

            <Separator />

            {/* Keyboard Navigation Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium flex items-center gap-2">
                <Keyboard className="h-4 w-4" />
                {t('accessibility.keyboard.label', 'ניווט מקלדת')}
              </label>
              <Button
                variant={settings.keyboardNavigation ? "default" : "outline"}
                size="sm"
                onClick={() => updateSetting('keyboardNavigation', !settings.keyboardNavigation)}
                aria-label={settings.keyboardNavigation
                  ? t('accessibility.keyboard.disable', 'השבת ניווט מקלדת')
                  : t('accessibility.keyboard.enable', 'הפעל ניווט מקלדת')
                }
                aria-pressed={settings.keyboardNavigation}
              >
                {settings.keyboardNavigation
                  ? t('accessibility.enabled', 'מופעל')
                  : t('accessibility.disabled', 'מושבת')
                }
              </Button>
            </div>

            <Separator />

            {/* Reset Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={resetSettings}
              className="w-full"
              aria-label={t('accessibility.reset.label', 'אפס הגדרות נגישות לברירת מחדל')}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {t('accessibility.reset.button', 'אפס הגדרות')}
            </Button>
          </div>

          {/* Keyboard shortcuts info */}
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <h3 className="text-sm font-medium mb-2">
              {t('accessibility.shortcuts.title', 'קיצורי מקלדת')}
            </h3>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>Alt + S: {t('accessibility.shortcuts.skipToContent', 'דלג לתוכן')}</li>
              <li>Tab: {t('accessibility.shortcuts.navigate', 'נווט בין אלמנטים')}</li>
              <li>Enter/Space: {t('accessibility.shortcuts.activate', 'הפעל אלמנט')}</li>
              <li>Esc: {t('accessibility.shortcuts.close', 'סגור תפריטים')}</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 